import React, {useState} from 'react';
import styles from './RequestForm.scss';
import blockStyles from "@baseStyles/block.scss";
import {useOperatorTaskContext} from "@contexts/ContextOperatorTaskProvider";
import {_validateEmail, _validateIsEmpty} from "@helpers/HelperValidator";
import Loading from "@components/widgets/Loading/Loading";
import TextBox from "@components/base/TextBox/TextBox";
import Button from "@components/base/Button/Button";
import ContentB4 from "@components/blocks/ContentB4/ContentB4";
import {notify} from "@contexts/ContextNotificationProvider";
import Select from "@components/base/Select/Select";
import CheckBox from "@components/base/CheckBox/CheckBox";
import {useScreenSizeBelowBreakpoint} from "@hooks/useScreenSizeBelowBreakpoint";
import useDataFileAction from "@hooks/useDataFileAction";

const RequestForm = ({data, color = ''}) => {

	if (!data) {
		return null;
	}

	const {
		formId,
		blockTitle,
		blockDesc,
		blockContent,
		successText,
		errorMsg,
		bookingWidget,
		noteData
	} = data;

	const {contextData} = useOperatorTaskContext();
	const {executeAction, ModalPortal} = useDataFileAction({color});
	const fields = blockContent?.fields ?? {};
	const phoneCodes = contextData.phoneCodes ?? [];
	const taskTypeTags = contextData.taskTypeTags ?? [];
	const defaultPhoneCode = 'BG'; // Default BG, +359

	// Runtime option sources a data-file field can reference via `optionsSource`.
	const selectOptionSources = {phoneCodes, taskTypeTags};

	const defaultFormState = Object.keys(fields).reduce((acc, key) => {
		acc[key] = fields[key].value ?? '';
		return acc;
	}, {});
	defaultFormState.phoneCode = '';

	// Error state holds messages only, so every entry starts as an empty string
	// (field values like `false` for checkboxes must not read as an error).
	const defaultErrorState = Object.keys(fields).reduce((acc, key) => {
		acc[key] = '';
		return acc;
	}, {});
	defaultErrorState.phoneCode = '';

	const [formState, setFormState] = useState({...defaultFormState, phoneCode: defaultPhoneCode});
	const [errorFormState, setErrorFormState] = useState({...defaultErrorState});
	const [loading, setLoading] = useState(false);
	// Keeps the booking config for the current session so the user can reopen the
	// popup after closing it (session is preserved on SimplyBook's side).
	const [savedBookingConfig, setSavedBookingConfig] = useState(null);

	const onChange = (key, value) => {
		// Set form state.
		formState[key] = value;
		setFormState({...formState});
		// Clear error state.
		errorFormState[key] = '';
		setErrorFormState({...errorFormState});
	}

	// Opens the SimplyBook modal through the data-file action hook.
	const openBookingModal = (bookingConfig) => {
		if (!bookingWidget?.Modal || !bookingConfig) {
			return;
		}
		executeAction({
			Modal: bookingWidget.Modal,
			modalParams: {title: bookingConfig.title, config: bookingConfig}
		});
	};

	// If a booking widget is configured and the trigger field matches (e.g. the
	// customer chose a video call), open the SimplyBook.me booking modal.
	const openBooking = () => {
		if (!bookingWidget?.company) {
			return;
		}
		if (formState[bookingWidget.triggerField] !== bookingWidget.triggerValue) {
			return;
		}
		// Resolve the international dialing code for the selected phone code so the
		// prefilled number is complete (defaults to Bulgaria's +359).
		const dialCode = phoneCodes
		  .flatMap((group) => group.options ?? [])
		  .find((opt) => String(opt.value) === String(formState.phoneCode))?.code ?? 359;

		// Prefill the widget with the client details entered in the form.
		const client = {
			name: formState.name,
			email: formState.email,
			phone: formState.phone ? `+${dialCode}${formState.phone}` : ''
		};

		const config = {
			title: bookingWidget.title,
			company: bookingWidget.company,
			theme: bookingWidget.theme,
			themeSettings: bookingWidget.themeSettings,
			predefined: {...bookingWidget.predefined, client},
			timeline: bookingWidget.timeline,
			datepicker: bookingWidget.datepicker,
			isRtl: bookingWidget.isRtl,
			appConfig: bookingWidget.appConfig
		};
		setSavedBookingConfig(config);
		openBookingModal(config);
	}

	const handleSuccess = () => {
		setLoading(false);
		setFormState({...defaultFormState, phoneCode: defaultPhoneCode});
		if (successText) {
			notify.success(successText.join('<br/>'), {
				duration: 20000 // 20 seconds
			});
		}
		openBooking();
	}

	const handleError = (message, originalMessage) => {
		setLoading(false);

		if (originalMessage === 'ALREADY_SUBMITTED_TASK') {
			setFormState({...defaultFormState, phoneCode: defaultPhoneCode});
		}

		notify.alert(message);
	}

	const submit = () => {
		//openBooking();
		setErrorFormState({...defaultErrorState});

		const current = {};
		Object.entries(fields).forEach(([key, field]) => {
			const emptyKey = `empty${key.charAt(0).toUpperCase()}${key.slice(1)}`;

			// Checkboxes hold a boolean; a required one must be checked.
			if (field.component === 'CheckBox') {
				if (field.required && formState[key] !== true) {
					current[key] = errorMsg[emptyKey] || '';
				}
				return;
			}

			const isEmpty = !_validateIsEmpty(formState[key]);

			if (field.required && isEmpty) {
				current[key] = errorMsg[emptyKey] || '';
			} else if (field.inputType === 'email' && !isEmpty && !_validateEmail(formState[key])) {
				current[key] = errorMsg.invalidEmail || '';
			}

			// Phone fields also require a selected phone code.
			if (field.component === 'Phone' && !_validateIsEmpty(formState.phoneCode)) {
				current.phoneCode = errorMsg.emptyPhoneCode || '';
			}
		});

		if (Object.keys(current).length !== 0) {
			setErrorFormState({...errorFormState, ...current});
			return;
		}

		setLoading(true);

		executeAction(
		  {
			  buttonAction: blockContent.buttonAction,
			  buttonParams: blockContent.buttonParams
		  },
		  {formData: formState, onSuccess: handleSuccess, onError: handleError}
		);
	}

	const isLessThan600 = useScreenSizeBelowBreakpoint(600);

	// Render the field input based on its declared component.
	const renderField = (key, field) => {
		switch (field.component) {
			case 'Select':
				return (
				  <Select
					selected={formState[key]}
					name={key}
					options={field.optionsSource ? (selectOptionSources[field.optionsSource] ?? []) : (field.options ?? [])}
					placeholder={field.placeholder}
					required={field.required}
					error={errorFormState[key]}
					color={color ? color : ''}
					onChange={(selectedOption) => onChange(key, selectedOption.value)}
				  />
				);
			case 'Phone':
				return (
				  <div className={styles.phoneContainer}>
					  <Select
						selected={formState.phoneCode}
						name={'phone_code'}
						options={selectOptionSources['phoneCodes']}
						placeholder={'999'}
						required={true}
						isGrouped={true}
						error={errorFormState.phoneCode}
						onChange={(selectedOption) => onChange('phoneCode', selectedOption.value)}
					  />
					  <TextBox
						type={field.inputType ?? 'tel'}
						name={key}
						placeholder={field.placeholder}
						value={formState[key]}
						error={errorFormState[key]}
						color={color ? color : ''}
						onChange={({target}) => onChange(key, target.value)}
					  />
				  </div>
				);
			case 'CheckBox':
				return (
				  <CheckBox
					name={key}
					label={field.label}
					checked={formState[key] === true}
					required={field.required}
					error={errorFormState[key]}
					onChange={(isChecked) => onChange(key, isChecked)}
				  />
				);
			case 'TextBox':
			default:
				return (
				  <TextBox
					type={field.inputType ?? 'text'}
					name={key}
					placeholder={field.placeholder}
					value={formState[key]}
					error={errorFormState[key]}
					color={color ? color : ''}
					onChange={({target}) => onChange(key, target.value)}
				  />
				);
		}
	};

	return (
	  <>
		  <div className={blockStyles.baseContainerHolder} id={formId}>
			  <div className={`${styles.container} ${color ? blockStyles[color] : ''}`}>
				  <h3>{blockTitle}</h3>
				  <p dangerouslySetInnerHTML={{__html: blockDesc}}/>

				  <div className={styles.formContainer}>
					  {Object.entries(fields).map(([key, field]) => {
						  // Fields without a title span the input column (no label on the left).
						  if (!field.title) {
							  return (
								<div className={styles.row} data-row-container="true" key={key}>
									<div className={styles.singleColumn}/>
									<div>
										{renderField(key, field)}
									</div>
								</div>
							  );
						  }

						  return (
							<div className={styles.row} data-row-container="true" key={key}>
								<div>
									<label>
										{field.required && <span className={styles.required}>*</span>}
										<span>{field.title}</span>
									</label>
								</div>
								<div>
									{renderField(key, field)}
								</div>
							</div>
						  );
					  })}

					  {/*Submit loading animation*/}
					  {loading &&
						<div className={styles.row}>
							<div className={styles.singleColumn}/>
							<div>
								<Loading centered={isLessThan600} color={color}/>
							</div>
						</div>
					  }

					  {/*Submit button*/}
					  <div className={styles.row}>
						  <div className={styles.singleColumn}/>
						  <div className={styles.btnColumn}>
							  <Button value={blockContent.buttonText} onClick={submit}/>
						  </div>
					  </div>

					  {/*Reopen booking button*/}
					  {savedBookingConfig &&
						<div className={styles.row}>
							<div className={styles.singleColumn}/>
							<div className={styles.btnColumn}>
								<Button
								  value={bookingWidget.reopenText}
								  layout={'transparent'}
								  onClick={() => openBookingModal(savedBookingConfig)}
								/>
							</div>
						</div>
					  }
				  </div>
			  </div>
		  </div>

		  {/* NOTES */}
		  {/*///////////////////////////////////////////////////////////////////////////////*/}
		  {noteData && noteData.length > 0 &&
			<div className={'baseMarginTop baseMarginLeft baseMarginRight'}>
				<ContentB4 data={noteData} size={'800'} color={color}/>
			</div>
		  }

		  {/* SimplyBook.me booking modal (opened via the data-file action hook) */}
		  {/*///////////////////////////////////////////////////////////////////////////////*/}
		  {ModalPortal}
	  </>
	);
}

export default RequestForm;
